import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../components/forms/button.component';
import Icon from '../../components/forms/icon.component';
import useChannelData from '../../hooks/use-channel-data.hook';
import { AlertService } from '../../services/alert.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { STORAGE } from '../../constants';
import Navigate from '../../components/cards/header-navigate.component';
import { AccountsService } from '../../services/accounts.service';
import useBgPages from '../../hooks/use-bg-pages.hook';
import SimpleDropdown from '../../components/datatable/simple-dropdown.component';
import Loader from '../../components/loader/loader.component';
import AddChannelModal from './modal/add-channel.modal';
import { t } from 'i18next';
import EditPriceModal from './modal/edit-price.modal';

interface Channel {
  config: string;
  price: number;
}

export interface Option {
  value: string;
  label: string;
}

export interface ChannelsFormValues {
  whatsapp: Channel[];
  rcs_conversational: Channel[];
  rcs_single: Channel[];
  rcs_basic: Channel[];
  sms: Channel[];
  email: Channel[];
}

const CHANNEL_CONFIG = {
  whatsapp: {
    icon: 'ki-whatsapp',
    title: 'WhatsApp',
    logo: '/assets/media/logos/meta.png',
    service: 'updateWAChannel',
    buttonText: 'account-page.add-account-btn',
    emptyText: 'account-page.no-accounts-assigned'
  },
  sms: {
    icon: 'ki-message-notif',
    title: 'SMS',
    logo: '/assets/media/logos/sms-logo.png',
    service: 'updateSMSChannel',
    buttonText: 'account-page.add-mask-btn',
    emptyText: 'account-page.no-masks-assigned'
  },
  email: {
    icon: 'ki-message-text-2',
    title: 'Email',
    logo: '/assets/media/logos/mail-logo.webp',
    service: 'updateEmailChannel',
    buttonText: 'account-page.add-account-btn',
    emptyText: 'account-page.no-accounts-assigned'
  },
  rcs_conversational: {
    icon: 'ki-messages',
    title: 'RCS Conversacional',
    logo: '/assets/media/logos/rcs-logo.png',
    service: 'updateRCSChannel',
    buttonText: 'account-page.add-agent-btn',
    emptyText: 'account-page.no-agents-assigned'
  },
  rcs_single: {
    icon: 'ki-messages',
    title: 'RCS Single',
    logo: '/assets/media/logos/rcs-logo.png',
    service: 'updateRCSChannel',
    buttonText: 'account-page.add-agent-btn',
    emptyText: 'account-page.no-agents-assigned'
  },
  rcs_basic: {
    icon: 'ki-messages',
    title: 'RCS Basic',
    logo: '/assets/media/logos/rcs-logo.png',
    service: 'updateRCSChannel',
    buttonText: 'account-page.add-agent-btn',
    emptyText: 'account-page.no-agents-assigned'
  }
};

const RCS_CONFIG = {
  rcs_conversational: { title: t('general.conversational'), rcsType: 'conversational' },
  rcs_single: { title: t('general.single'), rcsType: 'single' },
  rcs_basic: { title: t('general.basic'), rcsType: 'basic' }
};

declare const KTComponents: any;

const ChannelsConfigurator: React.FC<{ onSuccess: () => void; payload: any }> = ({
  payload,
  onSuccess
}) => {
  useBgPages();
  const { t } = useTranslation();
  const username = LocalStorageService.get(STORAGE.SESSION)?.username;
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [modalState, setModalState] = useState({ show: false, data: null as any });
  const [modalPriceState, setModalPriceState] = useState({ show: false, data: null as any });

  const { waAccounts, rcsAgents, smsOutputs, emailAccounts, isLoading, fetchAllData } =
    useChannelData(payload?.company?._id);

  const [channelsData, setChannelsData] = useState<ChannelsFormValues>(() => {
    const parseChannels = (channels: any[]) =>
      channels?.map((c) => ({ config: c.config._id, price: parseFloat(c.price.toString()) })) || [];

    return {
      whatsapp: parseChannels(payload?.channels?.whatsapp),
      rcs_conversational: parseChannels(payload?.channels?.rcs_conversational),
      rcs_single: parseChannels(payload?.channels?.rcs_single),
      rcs_basic: parseChannels(payload?.channels?.rcs_basic),
      sms: parseChannels(payload?.channels?.sms),
      email: parseChannels(payload?.channels?.email)
    };
  });

  const titleBolder = useMemo(
    () =>
      payload.company?.name && payload.name ? `${payload.company.name} - ${payload.name}` : '',
    [payload.company?.name, payload.name]
  );

  const getChannelOptions = useCallback(
    (channelName: keyof ChannelsFormValues): Option[] => {
      const optionsMap = {
        whatsapp: waAccounts,
        rcs_conversational: rcsAgents.CONVERSATIONAL,
        rcs_single: rcsAgents.SINGLE,
        rcs_basic: rcsAgents.BASIC,
        sms: smsOutputs,
        email: emailAccounts
      };
      return optionsMap[channelName] || [];
    },
    [waAccounts, rcsAgents, smsOutputs, emailAccounts]
  );

  const updateChannel = useCallback(
    async (channelName: keyof ChannelsFormValues, channels: Channel[]) => {
      const updateMap = {
        whatsapp: () =>
          AccountsService.updateWAChannel({ account: payload._id, username, whatsapp: channels }),
        sms: () =>
          AccountsService.updateSMSChannel({ account: payload._id, username, sms: channels }),
        email: () =>
          AccountsService.updateMailingChannel({ account: payload._id, username, email: channels }),
        rcs_conversational: () =>
          AccountsService.updateRCSChannel({
            account: payload._id,
            username,
            type: 'conversational',
            rcs: channels
          }),
        rcs_single: () =>
          AccountsService.updateRCSChannel({
            account: payload._id,
            username,
            type: 'single',
            rcs: channels
          }),
        rcs_basic: () =>
          AccountsService.updateRCSChannel({
            account: payload._id,
            username,
            type: 'basic',
            rcs: channels
          })
      };

      await updateMap[channelName]();
      setChannelsData((prev) => ({ ...prev, [channelName]: channels }));
    },
    [payload._id, username]
  );

  const handleRemoveAccount = useCallback(
    async (channelName: keyof ChannelsFormValues, configId: string) => {
      AlertService.warning(
        `<h2>${t('account-page.confirm-remove-account-title', {
          type:
            channelName === 'sms'
              ? t('general.mask')
              : channelName === 'email'
                ? t('general.email')
                : channelName.includes('rcs')
                  ? t('general.agent')
                  : t('general.account'),
          channel: t(`general.${channelName}`)
        })}</h2>`,
        t('account-page.confirm-remove-account-body'),
        async () => {
          try {
            const updatedChannels = channelsData[channelName].filter(
              (ch) => ch.config !== configId
            );
            await updateChannel(channelName, updatedChannels);
            await Promise.all([onSuccess(), fetchAllData()]);
            AlertService.toastSuccess(t('account-page.alert-success-remove'));
          } catch (error: any) {
            AlertService.toastError(error.error || error);
          }
        },
        t('general.continue')
      );
    },
    [channelsData, updateChannel, onSuccess, fetchAllData, t]
  );

  const handleDeactivateChannel = useCallback(
    async (channelName: keyof ChannelsFormValues) => {
      AlertService.warning(
        `<h2>${t('account-page.confirm-remove-channel-title')}</h2>`,
        t('account-page.confirm-remove-channel-body'),
        async () => {
          try {
            await updateChannel(channelName, []);
            AlertService.toastSuccess(t('account-page.alert-success-remove'));
          } catch (error: any) {
            AlertService.toastError(error.error || error);
          }
        },
        t('general.continue')
      );
    },
    [updateChannel, t]
  );

  const openModal = useCallback(
    (channelType: string) => {
      const options = getChannelOptions(channelType as keyof ChannelsFormValues);
      const existingConfigs =
        channelsData[channelType as keyof ChannelsFormValues]?.map((ch) => ch.config) || [];

      const existingChannels = channelsData[channelType as keyof ChannelsFormValues] || [];
      const availableOptions = options.filter((option) => !existingConfigs.includes(option.value));
      if (availableOptions.length === 0) {
        return AlertService.toastError(t('account-page.alert-no-available-accounts'));
      }
      setModalState({
        show: true,
        data: {
          channelType,
          options,
          existingConfigs,
          accountId: payload._id,
          existingChannels: existingChannels
        }
      });
    },
    [getChannelOptions, channelsData, payload._id]
  );

  const handleEditPrice = useCallback(
    (channelName: keyof ChannelsFormValues, configId: string, currentPrice: number) => {
      const options = getChannelOptions(channelName);
      const configName = options.find((opt) => opt.value === configId)?.label || 'Sin nombre';
      const existingChannels = channelsData[channelName] || [];

      setModalPriceState({
        show: true,
        data: {
          channelType: channelName,
          configId,
          currentPrice,
          configName,
          accountId: payload._id,
          accountName: payload.name,
          companyName: payload.company?.name,
          existingChannels: existingChannels
        }
      });
    },
    [getChannelOptions, payload._id, payload.name, payload.company?.name]
  );

  const onRefresh = useCallback(async () => {
    setIsLoadingLocal(true);
    try {
      await Promise.all([fetchAllData(), onSuccess()]);
    } finally {
      setIsLoadingLocal(false);
    }
  }, [fetchAllData, onSuccess]);

  const renderTable = useCallback(
    (channelName: keyof ChannelsFormValues, channels: Channel[]) => {
      const options = getChannelOptions(channelName);
      const getAccountName = (configId: string) =>
        options.find((opt) => opt.value === configId)?.label || 'Sin nombre';

      return (
        <div className="table-responsive">
          <table className="table table-row-bordered table-striped gs-4">
            <thead className="thead-light sticky-top">
              <tr className="fw-semibold fs-6 text-gray-800 border-bottom border-gray-200">
                <th>
                  {channelName === 'sms'
                    ? t('general.maskCapital')
                    : channelName === 'email'
                      ? 'Email'
                      : channelName.startsWith('rcs')
                        ? t('general.agentCapital')
                        : t('general.accountCapital')}
                </th>
                <th>{t('general.price')}</th>
                <th className="text-end">{t('general.acctions')}</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((row, index) => (
                <tr key={index}>
                  <td>
                    <strong className="text-dark fs-6">{getAccountName(row.config)}</strong>
                  </td>
                  <td>
                    <strong className="text-dark fs-6">${row.price}</strong>
                  </td>
                  <td className="text-end py-0 py-2">
                    <SimpleDropdown
                      options={[
                        {
                          label: t('general.edit'),
                          icon: 'ki-pencil',
                          color: 'warning',
                          onClick: () => handleEditPrice(channelName, row.config, row.price)
                        },
                        {
                          label: t('general.delete'),
                          icon: 'ki-trash',
                          color: 'danger',
                          onClick: () => handleRemoveAccount(channelName, row.config)
                        }
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
    [getChannelOptions, handleRemoveAccount, t]
  );

  const renderChannelCard = useCallback(
    (channelName: keyof ChannelsFormValues) => {
      const config = CHANNEL_CONFIG[channelName];
      const channels = channelsData[channelName];
      const options = getChannelOptions(channelName);

      return (
        <div className="card border-0 rounded-4 min-h-600px mh-600px" key={channelName}>
          <div className="d-flex justify-content-end align-items-center p-4 pb-0">
            {channels.length > 0 && (
              <Button
                type="button"
                className="btn btn-sm btn-light-danger"
                onClick={() => handleDeactivateChannel(channelName)}
              >
                <Icon icon="ki-minus-circle" size="fs-2" />
                {t('account-page.deactivate-account')}
              </Button>
            )}
          </div>
          <div className="card-body pt-5">
            <div className="row align-items-center">
              <div className="col-7 ps-xl-10">
                <div className="mb-6 pt-6">
                  <span className="fs-4 fw-semibold me-2 d-block lh-1 opacity-75">
                    {t('channels.channel')}
                  </span>
                  <span className="fs-1 fw-bold">{config.title}</span>
                </div>
              </div>
              <div className="col-5 mb-5">
                <div className="d-flex justify-content-end">
                  <img
                    alt={`${config.title} logo`}
                    src={config.logo}
                    width="80px"
                    className="hover-scale"
                  />
                </div>
              </div>
            </div>
            <div className="border-top pt-4 mt-4 px-2">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <strong className="card-title mb-0 text-info fs-4">
                  {t(
                    channelName === 'sms'
                      ? 'account-page.modal-add-account.stepper-3-input-mask'
                      : channelName.startsWith('rcs')
                        ? 'account-page.modal-add-account.stepper-3-input-agents'
                        : 'account-page.modal-add-account.stepper-3-input-accounts'
                  )}
                </strong>
                <Button
                  type="button"
                  className="btn btn-sm btn-light-info"
                  onClick={() => openModal(channelName)}
                >
                  <Icon icon="ki-plus-circle" size="fs-2" />
                  {t(config.buttonText)}
                </Button>
              </div>
              {(isLoading || isLoadingLocal) && <Loader />}
              {channels.length > 0 ? (
                renderTable(channelName, channels)
              ) : options.length === 0 ? (
                <div className="text-center py-10 mt-20">
                  <Icon icon="ki-minus-circle" size="fs-2x" type="danger" />
                  <p className="text-danger mt-3">
                    {t('account-page.alert-channel', {
                      type:
                        channelName === 'sms'
                          ? t('general.mask')
                          : channelName === 'email'
                            ? 'Email'
                            : channelName.startsWith('rcs')
                              ? t('general.agent')
                              : t('general.account')
                    })}
                  </p>
                </div>
              ) : (
                <div className="text-center py-10 mt-20">
                  <Icon icon="ki-information-5" size="fs-2x" />
                  <p className="text mt-3">{t(config.emptyText)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
    [channelsData, getChannelOptions, handleDeactivateChannel, openModal, renderTable, t]
  );

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    KTComponents.init();
  });

  useEffect(() => {
    if (payload?.channels) {
      const parseChannels = (channels: any[]) =>
        channels?.map((c) => ({ config: c.config._id, price: parseFloat(c.price.toString()) })) ||
        [];

      setChannelsData({
        whatsapp: parseChannels(payload.channels.whatsapp),
        rcs_conversational: parseChannels(payload.channels.rcs_conversational),
        rcs_single: parseChannels(payload.channels.rcs_single),
        rcs_basic: parseChannels(payload.channels.rcs_basic),
        sms: parseChannels(payload.channels.sms),
        email: parseChannels(payload.channels.email)
      });
    }
  }, [payload]);

  return (
    <div className="row g-xl-12">
      <Navigate
        title={t('account-page.modal-add-account.stepper-3-title1')}
        titleBolder={titleBolder}
        subtitle={t('account-page.configure-channels')}
        breadcrumbItems={['Operaciones', 'Cuentas']}
        btnBack
      />
      <div className="d-flex justify-content-start align-items-center mb-5">
        <button className="btn btn-light-info hover-scale fw-bolder me-4" onClick={onRefresh}>
          <Icon icon="ki-abstract-20" size="fs-1" />
          {t('general.update')}
        </button>
      </div>

      <div className="row px-0">
        <div className="col-lg-4 animate-fadeInUp animate-delay-1">
          {renderChannelCard('whatsapp')}
          <div className="mb-8"></div>
          {renderChannelCard('email')}
        </div>
        <div className="col-lg-4 animate-fadeInUp animate-delay-1">
          {renderChannelCard('sms')}
          <div className="mb-8"></div>
          {renderChannelCard('rcs_conversational')}
        </div>
        <div className="col-lg-4 animate-fadeInUp animate-delay-1">
          {renderChannelCard('rcs_single')}
          <div className="mb-8"></div>
          {renderChannelCard('rcs_basic')}
        </div>
      </div>

      <AddChannelModal
        show={modalState.show}
        onRequestClose={(refresh: boolean) => {
          if (refresh) {
            setTimeout(async () => {
              await Promise.all([onSuccess(), fetchAllData()]);
            }, 10);
          }
          setModalState({ show: false, data: null });
        }}
        payload={modalState.data}
      />
      <EditPriceModal
        show={modalPriceState.show}
        onRequestClose={(refresh: boolean) => {
          if (refresh) {
            setTimeout(async () => {
              await Promise.all([onSuccess(), fetchAllData()]);
            }, 10);
          }
          setModalPriceState({ show: false, data: null });
        }}
        payload={modalPriceState.data}
      />
    </div>
  );
};

export default ChannelsConfigurator;
